import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Building2, Upload, RotateCcw, Save, Loader2, Download, ChevronDown, History, Plus, Minus, Users, Undo2, Lightbulb, FileText, Clock } from "lucide-react";
import { BacklogHistoryDialog } from "@/components/BacklogHistoryDialog";
import { HistoryLogDialog } from "@/components/HistoryLogDialog";
import { SalaryAdjustmentsDialog } from "@/components/SalaryAdjustmentsDialog";
import { ComparisonDialog } from "@/components/ComparisonDialog";
import { SuggestionsDialog } from "@/components/SuggestionsDialog";
import { cn } from "@/lib/utils";

// Solid equivalents of translucent row backgrounds so sticky columns don't bleed on scroll
const SOLID_BG = {
  "bg-emerald-50/40": "bg-[#f7fefb]",
  "bg-emerald-50/60": "bg-[#f4fdf9]",
  "bg-emerald-100/50": "bg-[#e8fcf2]",
  "bg-emerald-100/60": "bg-[#e3fcef]",
  "bg-amber-50/40": "bg-[#fffdf7]",
  "bg-orange-200/70": "bg-[#fee3c3]",
  "bg-sky-100/50": "bg-[#eff8fe]",
  "bg-sky-50/40": "bg-[#f9fcff]",
};

const fmtK = (v) => {
  if (v == null) return "—";
  const n = Math.round(v);
  const neg = n < 0;
  const a = Math.abs(n);
  const s = a >= 1000000 ? `${(a / 1000000).toFixed(2)}M` : a >= 1000 ? `${(a / 1000).toFixed(0)}K` : `${a}`;
  return `${neg ? "-" : ""}$${s}`;
};

const fmtKAbs = (v) => {
  const a = Math.abs(Math.round(v || 0));
  if (a >= 1000000) return `$${(a / 1000000).toFixed(2)}M`;
  if (a >= 1000) return `$${Math.round(a / 1000)}K`;
  return `$${a}`;
};

function KCell({ value, locked, disabled, title, onCommit, testid }) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <span className="inline-flex items-center gap-0.5">
        <input
          autoFocus
          type="number"
          step="10"
          defaultValue={Math.round(value / 1000)}
          onBlur={(e) => { setEditing(false); onCommit(e.target.value); }}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") setEditing(false); }}
          className="w-[52px] h-6 text-[11px] text-right border border-[#007BFF]/60 rounded px-1 bg-white focus:outline-none focus:ring-1 focus:ring-[#007BFF] tabular-nums"
          data-testid={`${testid}-edit`}
        />
        <span className="text-[9px] text-slate-400">K</span>
      </span>
    );
  }
  return (
    <button
      disabled={disabled}
      onClick={() => setEditing(true)}
      title={title}
      className={cn(
        "h-6 min-w-[38px] text-[11px] text-right px-1 rounded tabular-nums whitespace-nowrap",
        disabled ? "text-slate-300 cursor-not-allowed" : value < -0.5 ? "text-red-600" : value > 0.5 ? "text-emerald-600" : "text-slate-400",
        locked && !disabled && "font-bold ring-1 ring-[#007BFF]/50 bg-white",
        !disabled && "hover:bg-white hover:ring-1 hover:ring-slate-300"
      )}
      data-testid={testid}
    >
      {disabled ? "—" : fmtKAbs(value)}
    </button>
  );
}

const VIEWS = [
  { id: "rolling12", label: "Rolling 12" },
  { id: "rolling24", label: "Rolling 24" },
  { id: "this_fy", label: "This FY" },
  { id: "next_fy", label: "Next FY" },
  { id: "board", label: "Board" },
];

function PctInput({ value, onChange, testid, className }) {
  return (
    <div className={cn("flex items-center gap-0.5", className)}>
      <input
        key={value}
        type="number"
        step="0.1"
        defaultValue={value}
        onBlur={(e) => {
          const v = e.target.value === "" ? 0 : parseFloat(e.target.value);
          if (!Number.isNaN(v) && v !== value) onChange(v);
        }}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        className="w-14 h-6 text-[11px] text-right border border-slate-200 rounded px-1 py-0 bg-white focus:outline-none focus:ring-1 focus:ring-[#007BFF] tabular-nums"
        data-testid={testid}
      />
      <span className="text-[10px] text-slate-400">%</span>
    </div>
  );
}

function BandInput({ value, onChange, disabled, testid }) {
  const mVal = Math.round(((value || 0) / 1000000) * 100) / 100;
  return (
    <div className="flex items-center gap-0.5">
      <span className="text-[10px] text-slate-400">$</span>
      <input
        key={mVal}
        type="number"
        step="0.1"
        defaultValue={mVal}
        disabled={disabled}
        onBlur={(e) => {
          const v = e.target.value === "" ? 0 : parseFloat(e.target.value);
          if (!Number.isNaN(v) && v !== mVal) onChange(v * 1000000);
        }}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        className="w-[52px] h-7 text-xs text-right border border-slate-200 rounded-md px-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#007BFF] tabular-nums disabled:bg-slate-100 disabled:text-slate-400"
        data-testid={testid}
      />
      <span className="text-[10px] text-slate-400">M</span>
    </div>
  );
}

function MonthlyInput({ value, onChange, testid }) {
  const kVal = Math.round((value || 0) / 100) / 10;
  return (
    <div className="flex items-center gap-0.5 justify-end">
      <span className="text-[10px] text-slate-400">$</span>
      <input
        key={kVal}
        type="number"
        step="1"
        defaultValue={kVal}
        onBlur={(e) => {
          const v = e.target.value === "" ? 0 : parseFloat(e.target.value);
          if (!Number.isNaN(v) && v !== kVal) onChange(v * 1000);
        }}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        className="w-[52px] h-6 text-[11px] text-right border border-slate-200 rounded px-1 py-0 bg-white focus:outline-none focus:ring-1 focus:ring-[#007BFF] tabular-nums"
        data-testid={testid}
      />
      <span className="text-[9px] text-slate-400">K/mo</span>
    </div>
  );
}

export default function CompanyForecast() {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [view, setView] = useState("rolling12");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [changeLogOpen, setChangeLogOpen] = useState(false);
  const [adjOpen, setAdjOpen] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [expanded, setExpanded] = useState({ direct: false, indirect: false, overhead: false });
  const [history, setHistory] = useState([]);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [uploadingActuals, setUploadingActuals] = useState(false);
  const actualsFileRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await api.getCompanyForecast();
      setData(d);
      setSettings(d.settings);
      setDirty(false);
      setHistory([]);
    } catch (e) {
      if (e?.response?.status === 403) {
        setForbidden(true);
        return;
      }
      console.error(e);
      toast.error("Failed to load company forecast");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pushHistory = () => setHistory((h) => [...h.slice(-49), settings]);

  const undo = useCallback(() => {
    setHistory((h) => {
      if (!h.length) return h;
      setSettings(h[h.length - 1]);
      setDirty(h.length > 1);
      return h.slice(0, -1);
    });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === "z") {
        const t = e.target;
        if (t?.tagName === "INPUT" || t?.tagName === "TEXTAREA" || t?.isContentEditable) return;
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo]);

  const patch = (p) => { pushHistory(); setSettings((s) => ({ ...s, ...p })); setDirty(true); };
  const patchItem = (listKey, idx, patchObj) => {
    pushHistory();
    setSettings((s) => {
      const items = s[listKey].map((it, i) => (i === idx ? { ...it, ...patchObj } : it));
      return { ...s, [listKey]: items };
    });
    setDirty(true);
  };

  const patchMonthly = (key, monthKey, v) => {
    pushHistory();
    setSettings((s) => {
      const next = { ...(s[key] || {}) };
      if (v == null) delete next[monthKey];
      else next[monthKey] = v;
      return { ...s, [key]: next };
    });
    setDirty(true);
  };

  const patchDirectItem = (idx, pct) => {
    pushHistory();
    setSettings((s) => {
      const items = s.direct_items.map((it, i) => (i === idx ? { ...it, pct } : it));
      const total = Math.round(items.reduce((x, it) => x + (it.pct || 0), 0) * 100) / 100;
      return { ...s, direct_items: items, direct_costs_pct: total };
    });
    setDirty(true);
  };

  const setDirectTotal = (newTotal) => {
    pushHistory();
    setSettings((s) => {
      const old = s.direct_items.reduce((sum, it) => sum + (it.pct || 0), 0);
      let items;
      if (old > 0) {
        items = s.direct_items.map((it) => ({ ...it, pct: Math.round(((it.pct || 0) * newTotal / old) * 100) / 100 }));
      } else {
        const per = Math.round((newTotal / s.direct_items.length) * 100) / 100;
        items = s.direct_items.map((it) => ({ ...it, pct: per }));
      }
      const drift = Math.round((newTotal - items.reduce((sum, it) => sum + it.pct, 0)) * 100) / 100;
      if (drift !== 0 && items.length) {
        let maxI = 0;
        items.forEach((it, i) => { if (it.pct > items[maxI].pct) maxI = i; });
        items[maxI] = { ...items[maxI], pct: Math.round((items[maxI].pct + drift) * 100) / 100 };
      }
      return { ...s, direct_items: items, direct_costs_pct: newTotal };
    });
    setDirty(true);
  };

  const setCategoryTotal = (listKey) => (newTotal) => {
    pushHistory();
    setSettings((s) => {
      const old = s[listKey].reduce((sum, it) => sum + (it.pct || 0), 0);
      let items;
      if (old > 0) {
        items = s[listKey].map((it) => ({ ...it, pct: Math.round(((it.pct || 0) * newTotal / old) * 100) / 100 }));
      } else {
        const per = Math.round((newTotal / s[listKey].length) * 100) / 100;
        items = s[listKey].map((it) => ({ ...it, pct: per }));
      }
      const drift = Math.round((newTotal - items.reduce((sum, it) => sum + it.pct, 0)) * 100) / 100;
      if (drift !== 0 && items.length) {
        let maxI = 0;
        items.forEach((it, i) => { if (it.pct > items[maxI].pct) maxI = i; });
        items[maxI] = { ...items[maxI], pct: Math.round((items[maxI].pct + drift) * 100) / 100 };
      }
      return { ...s, [listKey]: items };
    });
    setDirty(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const r = await api.uploadBacklog(file);
      toast.success(`Backlog loaded: ${r.line_count.toLocaleString()} lines · ${r.project_count} projects · ${fmtK(r.total_remaining)} remaining`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleActualsUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingActuals(true);
    try {
      const r = await api.uploadActuals(file);
      toast.success(`Actuals mapped to ${r.month_key} — Revenue ${Math.round(r.revenue / 1000)}K, EBITDA ${Math.round(r.ebitda / 1000)}K`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not parse the income statement PDF");
    } finally {
      setUploadingActuals(false);
      e.target.value = "";
    }
  };

  const saveScenario = async () => {
    setSaving(true);
    try {
      await api.updateForecastSettings(settings);
      toast.success("Scenario saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!dirty || !settings) return;
    const t = setTimeout(() => {
      api.updateForecastSettings(settings).then(() => setDirty(false)).catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
  }, [settings, dirty]);

  const revertDefaults = async () => {
    if (!window.confirm("Revert all forecast inputs to the locked-in baseline defaults?")) return;
    try {
      await api.resetForecastSettings();
      await load();
      toast.success("Reverted to baseline defaults");
    } catch (e) {
      toast.error("Revert failed");
    }
  };

  const revertLast = async () => {
    if (dirty) {
      await load();
      toast.success("Unsaved changes discarded");
      return;
    }
    try {
      await api.revertLastForecastSettings();
      await load();
      toast.success("Reverted to last saved settings");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nothing to revert to");
    }
  };

  const doExport = async (exportView) => {
    setExporting(true);
    try {
      const blob = await api.exportForecast(exportView);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportView === "board" ? "board_summary.xlsx" : `company_forecast_${exportView}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  // --- P&L computation over the FULL axis (client-side for instant what-if) ---
  const calc = useMemo(() => {
    if (!data || !settings) return null;
    const n = data.months.length;
    const ci = data.current_index || 0;
    const adj = 1 + (settings.revenue_adjustment_pct || 0) / 100;
    const obMonths = Math.max(1, settings.overbilling_months || 1);
    const obPer = (settings.overbilling_total || 0) / obMonths;
    const sbMonths = Math.max(1, settings.service_backlog_months || 1);
    const sbPer = (settings.service_backlog_total || 0) / sbMonths;
    const itdaMo = (settings.itda_annual || 0) / 12;

    const backlog = data.backlog_revenue.map((v) => v * adj);
    const sales = data.months.map((_, i) => (data.sales_revenue || [])[i] || 0);
    const overbilling = Array.from({ length: n }, (_, i) => (i >= ci && i < ci + obMonths ? obPer : 0));
    const serviceBacklog = Array.from({ length: n }, (_, i) => (i >= ci && i < ci + sbMonths ? sbPer : 0));
    const actuals = data.months.map((m) => (settings.actuals || {})[m.key] || 0);
    // Auto band logic: New Sales fills shortfalls up to the month's cap (from month 4+);
    // Revenue Adjustment defers peaks forward. Manual overrides in either row are locked.
    const adjOverrides = settings.revenue_adjustments || {};
    const nsOverrides = settings.new_sales || {};
    const smoothOn = settings.smooth_enabled !== false;
    const lo = Number(settings.smooth_min) || 1700000;
    const hi = Number(settings.smooth_max) || 2200000;
    const capFor = (key) => {
      const [y, mo] = key.split("-").map(Number);
      const x = Math.sin(y * 12 + mo) * 10000;
      return lo + (x - Math.floor(x)) * Math.max(hi - lo, 0);
    };
    const newSales = Array.from({ length: n }, () => 0);
    const revAdjust = Array.from({ length: n }, () => 0);
    let carry = 0;
    for (let i = ci; i < n; i++) {
      const key = data.months[i].key;
      const cap = capFor(key);
      const rawBase = backlog[i] + sales[i] + overbilling[i] + serviceBacklog[i] + actuals[i];
      if (i >= ci + 3) {
        if (nsOverrides[key] != null) newSales[i] = Number(nsOverrides[key]);
        else if (smoothOn) newSales[i] = Math.max(0, cap - (rawBase + carry));
      }
      const raw = rawBase + newSales[i];
      const available = raw + carry;
      let recognized;
      if (adjOverrides[key] != null) {
        recognized = raw + Number(adjOverrides[key]);
      } else if (smoothOn) {
        recognized = i === n - 1 ? available : Math.min(available, cap);
      } else {
        recognized = raw;
      }
      revAdjust[i] = recognized - raw;
      carry = available - recognized;
    }
    const revenue = backlog.map((v, i) => v + sales[i] + overbilling[i] + serviceBacklog[i] + newSales[i] + revAdjust[i] + actuals[i]);

    const rowPct = (pct) => revenue.map((r) => (r * pct) / 100);
    const direct = rowPct(settings.direct_costs_pct || 0);
    const directRows = (settings.direct_items || []).map((it) => ({ ...it, vals: rowPct(it.pct) }));
    const indirectRows = settings.indirect_items.map((it) => ({ ...it, vals: rowPct(it.pct) }));
    const overheadRows = settings.overhead_items.map((it) => ({ ...it, vals: Array.from({ length: n }, () => it.monthly || 0) }));

    // Salary adjustment overlays: signed annual/12 on the matching category, from its start month (default current)
    const monthKeys = data.months.map((m) => m.key);
    (settings.salary_adjustments || []).forEach((adj) => {
      const perMo = (adj.annual || 0) / 12;
      let startI = ci;
      if (adj.start && monthKeys.includes(adj.start)) startI = Math.max(monthKeys.indexOf(adj.start), ci);
      const target = overheadRows.find((r) => r.name === adj.category) || indirectRows.find((r) => r.name === adj.category);
      if (target) {
        for (let i = startI; i < n; i++) target.vals[i] += perMo;
      }
    });

    const directGM = revenue.map((r, i) => r - direct[i]);
    const indirectTotal = revenue.map((_, i) => indirectRows.reduce((s, r) => s + r.vals[i], 0));
    const gross = revenue.map((r, i) => r - direct[i] - indirectTotal[i]);
    let overheadTotal = revenue.map((_, i) => overheadRows.reduce((s, r) => s + r.vals[i], 0));

    // Company bonus: % of rolling-12 overhead, fiscal quarter-end months (Jul/Oct/Jan/Apr)
    const bonusCfg = settings.bonus || {};
    const bonusBase = overheadTotal.slice(ci, ci + 12).reduce((s, v) => s + v, 0);
    const bonusAutoQ = (bonusBase * (bonusCfg.pct || 0)) / 100 / 4;
    const bonusRow = Array.from({ length: n }, () => 0);
    if (bonusCfg.enabled) {
      const qmap = { 2: 0, 5: 1, 8: 2, 11: 3 };
      const quarters = bonusCfg.quarters || [];
      for (let i = ci; i < n; i++) {
        const qIdx = qmap[i % 12];
        if (qIdx == null || !quarters[qIdx]) continue;
        const q = quarters[qIdx];
        if (q.on) bonusRow[i] = q.amount != null && q.amount !== "" ? Number(q.amount) : bonusAutoQ;
      }
    }
    overheadTotal = overheadTotal.map((v, i) => v + bonusRow[i]);
    const ebitda = gross.map((g, i) => g - overheadTotal[i]);
    const itdaPct = settings.itda_pct;
    const itda = itdaPct != null
      ? revenue.map((r) => (r * itdaPct) / 100)
      : Array.from({ length: n }, () => itdaMo);
    const net = ebitda.map((e, i) => e - itda[i]);

    // Uploaded month actuals override the forecast column totals
    const am = data.actuals_map || {};
    data.months.forEach((m, i) => {
      const a = am[m.key];
      if (!a) return;
      revenue[i] = a.revenue;
      direct[i] = a.direct;
      directGM[i] = a.revenue - a.direct;
      indirectTotal[i] = a.indirect;
      gross[i] = directGM[i] - a.indirect;
      overheadTotal[i] = a.overhead;
      ebitda[i] = a.ebitda;
      itda[i] = a.itda;
      net[i] = a.net;
    });

    return {
      backlog, overbilling, serviceBacklog, newSales, revAdjust, actuals, revenue, direct, directRows, directGM, sales,
      indirectRows, indirectTotal, gross, overheadRows, overheadTotal, ebitda, itda, net,
      bonusRow, bonusAutoQ, bonusBase, bonusEnabled: !!bonusCfg.enabled,
      indirectPctSum: settings.indirect_items.reduce((s, it) => s + (it.pct || 0), 0),
    };
  }, [data, settings]);

  if (loading || forbidden) return null;

  const hasData = (data?.meta?.line_count || 0) > 0;
  const ci = data?.current_index || 0;
  const n = data?.months?.length || 0;
  const ranges = {
    rolling12: [ci, Math.min(ci + 12, n)],
    rolling24: [ci, Math.min(ci + 24, n)],
    this_fy: [0, 12],
    next_fy: [12, 24],
    board: [ci, Math.min(ci + 12, n)],
  };
  const [vs, ve] = ranges[view];
  const months = data?.months?.slice(vs, ve) || [];
  const w = (arr) => arr.slice(vs, ve);
  const sum = (arr) => arr.reduce((s, v) => s + v, 0);
  const wsum = (arr) => sum(w(arr));
  const wRevTot = calc ? wsum(calc.revenue) : 0;
  const wpct = (arr) => (wRevTot ? (wsum(arr) / wRevTot) * 100 : 0);
  const viewLabel = VIEWS.find((v) => v.id === view)?.label;

  const pctLabel = (v, bold) => (
    <span className={cn("text-[10px] tabular-nums", bold ? "font-bold text-slate-700" : "text-slate-400")}>{v.toFixed(1)}%</span>
  );

  const Row = ({ label, indent, arr, bold, pctCell, bg, red, testid, onToggle, isExpanded, pctOfArr }) => {
    const vals = w(arr);
    const pofs = pctOfArr ? w(pctOfArr) : null;
    const stickyBg = SOLID_BG[bg] || "bg-white";
    return (
      <tr className={cn("border-b border-slate-100", bg)} data-testid={testid}>
        <td className={cn("px-3 py-1.5 text-xs whitespace-nowrap sticky left-0 z-10", stickyBg, indent && "pl-7", bold ? "font-bold text-slate-900" : "text-slate-600")}>
          {onToggle && (
            <button
              onClick={onToggle}
              className="mr-1.5 inline-flex items-center justify-center w-4 h-4 rounded border border-slate-400/60 bg-white/70 text-slate-600 hover:bg-white align-[-2px]"
              data-testid={`${testid}-toggle`}
            >
              {isExpanded ? <Minus className="h-2.5 w-2.5" /> : <Plus className="h-2.5 w-2.5" />}
            </button>
          )}
          {label}
        </td>
        <td className={cn("px-2 py-1.5 text-center sticky left-[190px] z-10 border-r border-slate-200", stickyBg)}>{pctCell}</td>
        {vals.map((v, i) => (
          <td key={i} className={cn(
            "px-1.5 py-1.5 text-right tabular-nums whitespace-nowrap text-[11px]",
            bold ? "font-bold text-slate-900" : "text-slate-600",
            red && v < -0.5 && "text-red-600"
          )}>
            {fmtK(v)}
            {pofs && (
              <div className="text-[9px] font-normal text-slate-400" data-testid={`${testid}-pct-${i}`}>
                {pofs[i] ? ((v / pofs[i]) * 100).toFixed(1) : "0.0"}%
              </div>
            )}
          </td>
        ))}
        <td className={cn(
          "px-2 py-1.5 text-right tabular-nums whitespace-nowrap border-l-2 border-slate-200 bg-slate-50/80 text-xs",
          bold ? "font-bold text-slate-900" : "font-medium text-slate-700",
          red && sum(vals) < -0.5 && "text-red-600"
        )}>
          {fmtK(sum(vals))}
          {pofs && (
            <div className="text-[9px] font-normal text-slate-400" data-testid={`${testid}-pct-total`}>
              {sum(pofs) ? ((sum(vals) / sum(pofs)) * 100).toFixed(1) : "0.0"}% avg
            </div>
          )}
        </td>
      </tr>
    );
  };

  const AdjustmentRow = ({ label, arr, testid, settingsKey = "revenue_adjustments", lockUntil }) => {
    const vals = w(arr);
    const overrides = settings[settingsKey] || {};
    const minEditable = lockUntil ?? ci;
    return (
      <tr className="border-b border-slate-100 bg-emerald-50/40" data-testid={testid}>
        <td className="px-3 py-1.5 text-xs whitespace-nowrap sticky left-0 z-10 bg-[#f7fefb] text-slate-600">{label}</td>
        <td className="px-2 py-1.5 text-center sticky left-[190px] z-10 border-r border-slate-200 bg-[#f7fefb]">{pctLabel(wpct(arr))}</td>
        {months.map((m, i) => {
          const axisIdx = vs + i;
          const locked = overrides[m.key] != null;
          const actualized = !!(data.actuals_map || {})[m.key];
          const disabled = axisIdx < minEditable || actualized;
          const val = Math.round(vals[i]);
          return (
            <td key={`${m.key}-${val}`} className="px-1 py-1 text-right">
              <KCell
                value={val}
                locked={locked}
                disabled={disabled}
                title={actualized ? "Locked — actuals uploaded for this month; re-upload actuals to change it" : locked ? "Locked manual value — clear the cell to return to auto" : "Click to edit (value in $K) — sign follows what you type"}
                onCommit={(raw) => {
                  if (raw === "") { if (locked) patchMonthly(settingsKey, m.key, null); return; }
                  const v = parseFloat(raw) * 1000;
                  if (!Number.isNaN(v) && Math.round(v) !== val) patchMonthly(settingsKey, m.key, v);
                }}
                testid={`${testid}-input-${i}`}
              />
            </td>
          );
        })}
        <td className={cn(
          "px-2 py-1.5 text-right tabular-nums whitespace-nowrap border-l-2 border-slate-200 bg-slate-50/80 text-xs font-medium",
          sum(vals) < -0.5 ? "text-red-600" : sum(vals) > 0.5 ? "text-emerald-600" : "text-slate-700"
        )}>
          {fmtKAbs(sum(vals))}
        </td>
      </tr>
    );
  };

  const PastOnlyRow = ({ label, arr, diff, testid }) => {
    const pastVals = w(arr).map((v, i) => (vs + i < ci ? v : null));
    const total = pastVals.reduce((s, v) => s + (v || 0), 0);
    return (
      <tr className="border-b border-slate-100 bg-sky-50/40" data-testid={testid}>
        <td className="px-3 py-1.5 text-xs whitespace-nowrap sticky left-0 z-10 bg-[#f9fcff] pl-7 text-slate-500 italic">{label}</td>
        <td className="px-2 py-1.5 text-center sticky left-[190px] z-10 border-r border-slate-200 bg-[#f9fcff]">
          {pctLabel(wRevTot ? (total / wRevTot) * 100 : 0)}
        </td>
        {pastVals.map((v, i) => (
          <td key={i} className={cn(
            "px-1.5 py-1.5 text-right tabular-nums whitespace-nowrap text-[11px]",
            v == null ? "text-slate-300" : diff ? (v < -0.5 ? "text-red-600 font-semibold" : "text-emerald-600 font-semibold") : "text-slate-500 italic"
          )}>
            {v == null ? "—" : fmtK(v)}
          </td>
        ))}
        <td className={cn(
          "px-2 py-1.5 text-right tabular-nums whitespace-nowrap border-l-2 border-slate-200 bg-slate-50/80 text-xs font-medium",
          diff ? (total < -0.5 ? "text-red-600" : "text-emerald-600") : "text-slate-500"
        )}>
          {fmtK(total)}
        </td>
      </tr>
    );
  };

  const EditableMoneyRow = ({ label, settingsKey, arr, testid, pastOnly, lockUntil }) => {
    const vals = w(arr);
    return (
      <tr className="border-b border-slate-100 bg-emerald-50/40" data-testid={testid}>
        <td className="px-3 py-1.5 text-xs whitespace-nowrap sticky left-0 z-10 bg-[#f7fefb] text-slate-600">{label}</td>
        <td className="px-2 py-1.5 text-center sticky left-[190px] z-10 border-r border-slate-200 bg-[#f7fefb]">
          {pctLabel(wpct(arr))}
        </td>
        {months.map((m, i) => {
          const axisIdx = vs + i;
          const locked = lockUntil != null && axisIdx < lockUntil;
          const actualized = !!(data.actuals_map || {})[m.key];
          const disabled = (pastOnly && axisIdx >= ci) || locked || actualized;
          return (
            <td key={`${m.key}-${(settings[settingsKey] || {})[m.key] ?? ""}`} className="px-1 py-1 text-right">
              <input
                type="number"
                step="10000"
                defaultValue={locked ? "" : (settings[settingsKey] || {})[m.key] ?? ""}
                placeholder={disabled ? "—" : "0"}
                disabled={disabled}
                title={locked ? "New sales can't land within 3 months of today" : pastOnly && disabled ? "Actuals can only be entered for past months" : undefined}
                onBlur={(e) => {
                  const v = e.target.value === "" ? 0 : parseFloat(e.target.value);
                  if (!Number.isNaN(v) && v !== ((settings[settingsKey] || {})[m.key] || 0)) patchMonthly(settingsKey, m.key, v);
                }}
                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                className={cn(
                  "w-[64px] h-6 text-[11px] text-right border border-slate-200 rounded px-1 bg-white focus:outline-none focus:ring-1 focus:ring-[#007BFF] tabular-nums",
                  vals[i] < 0 && "text-red-600",
                  disabled && "bg-slate-100 text-slate-300 cursor-not-allowed"
                )}
                data-testid={`${testid}-input-${i}`}
              />
            </td>
          );
        })}
        <td className={cn(
          "px-2 py-1.5 text-right tabular-nums whitespace-nowrap border-l-2 border-slate-200 bg-slate-50/80 text-xs font-medium text-slate-700",
          sum(vals) < -0.5 && "text-red-600"
        )}>
          {fmtK(sum(vals))}
        </td>
      </tr>
    );
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200" data-testid="company-forecast">
      {/* Header */}
      <div className="p-5 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Building2 className="h-4 w-4 text-sky-600" />
          Company Forecast
          {data?.meta && (
            <span className="text-xs font-normal text-slate-400 ml-1" data-testid="forecast-meta">
              {data.meta.filename} · {new Date(data.meta.uploaded_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })} · {fmtK(data.total_remaining)} backlog
              {data.past_due_total > 0 && <span className="text-amber-600"> · {fmtK(data.past_due_total)} past-due pushed forward</span>}
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          {/* View toggle */}
          <div className="flex rounded-md border border-slate-200 overflow-hidden" data-testid="forecast-view-toggle">
            {VIEWS.map((v) => (
              <button
                key={v.id}
                onClick={() => setView(v.id)}
                className={cn(
                  "px-3 h-8 text-xs font-medium transition-colors",
                  view === v.id ? "bg-[#007BFF] text-white" : "bg-white text-slate-600 hover:bg-slate-50"
                )}
                data-testid={`view-${v.id}`}
              >
                {v.label}
              </button>
            ))}
          </div>
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={handleUpload} data-testid="backlog-file-input" />
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" disabled={uploading}
            onClick={() => fileRef.current?.click()} data-testid="upload-backlog-btn">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {uploading ? "Parsing…" : "Backlog"}
          </Button>
          <input ref={actualsFileRef} type="file" accept=".pdf" className="hidden" onChange={handleActualsUpload} data-testid="actuals-file-input" />
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" disabled={uploadingActuals}
            onClick={() => actualsFileRef.current?.click()} data-testid="upload-actuals-btn">
            {uploadingActuals ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {uploadingActuals ? "Parsing…" : "Actuals"}
          </Button>
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => setHistoryOpen(true)} data-testid="backlog-history-btn">
            <History className="h-3.5 w-3.5" />
            Backlog
          </Button>
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => setComparisonOpen(true)} data-testid="comparison-btn">
            <History className="h-3.5 w-3.5" />
            Comparison
          </Button>
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => setSuggestOpen(true)} aria-label={`Path to ${settings.ebitda_target_pct ?? 10}% EBITDA`} title={`Path to ${settings.ebitda_target_pct ?? 10}% EBITDA`} data-testid="suggestions-btn">
            <Lightbulb className="h-3.5 w-3.5" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" disabled={exporting || !hasData} data-testid="forecast-export-btn">
                {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                Export
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => doExport(view)} data-testid="export-current-view">Current View ({viewLabel})</DropdownMenuItem>
              <DropdownMenuItem onClick={() => doExport("full")} data-testid="export-full">Full Forecast (all months)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => doExport("board")} data-testid="export-board">Board Summary</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => setAdjOpen(true)} data-testid="salary-adjustments-btn">
            <Users className="h-3.5 w-3.5" />
            Adjustments
            {(settings?.salary_adjustments?.length || 0) > 0 && (
              <span className="ml-0.5 text-[10px] font-bold text-[#007BFF]">{settings.salary_adjustments.length}</span>
            )}
          </Button>
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => setChangeLogOpen(true)} data-testid="forecast-history-btn">
            <Clock className="h-3.5 w-3.5" />
            History
          </Button>
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={undo} disabled={history.length === 0}
            title="Undo last change (Ctrl/Cmd+Z)" data-testid="forecast-undo-btn">
            <Undo2 className="h-3.5 w-3.5" />
            Undo
          </Button>
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={revertLast} data-testid="forecast-revert-last-btn">
            <Undo2 className="h-3.5 w-3.5" />
            Revert to Last
          </Button>
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={revertDefaults} data-testid="forecast-revert-btn">
            <RotateCcw className="h-3.5 w-3.5" />
            Revert to Default
          </Button>
          <Button size="sm" className="h-8 gap-1.5 text-xs bg-[#007BFF] hover:bg-[#0069d9] text-white" disabled={!dirty || saving}
            onClick={saveScenario} data-testid="forecast-save-btn">
            <Save className="h-3.5 w-3.5" />
            {saving ? "Saving…" : "Save Scenario"}
          </Button>
        </div>
      </div>

      {!hasData ? (
        <div className="p-10 text-center" data-testid="forecast-empty">
          <p className="text-sm text-slate-500 font-medium">No backlog report loaded yet</p>
          <p className="text-xs text-slate-400 mt-1">Upload the Procore Backlog Report (.xlsx) to build the company forecast</p>
        </div>
      ) : calc && (
        <>
          {/* Assumption inputs */}
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex items-end gap-5 flex-wrap" data-testid="forecast-inputs">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">Overbilling $</div>
              <Input type="number" step="100000" value={settings.overbilling_total}
                onChange={(e) => patch({ overbilling_total: e.target.value === "" ? 0 : parseFloat(e.target.value) })}
                className="h-7 w-28 text-xs" data-testid="input-overbilling-total" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">Overbilling Months</div>
              <Input type="number" min={1} max={36} value={settings.overbilling_months}
                onChange={(e) => patch({ overbilling_months: e.target.value === "" ? 1 : parseInt(e.target.value) })}
                className="h-7 w-16 text-xs" data-testid="input-overbilling-months" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">Service Backlog $</div>
              <Input type="number" step="100000" value={settings.service_backlog_total ?? 0}
                onChange={(e) => patch({ service_backlog_total: e.target.value === "" ? 0 : parseFloat(e.target.value) })}
                className="h-7 w-28 text-xs" data-testid="input-service-backlog-total" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">Service Backlog Months</div>
              <Input type="number" min={1} max={36} value={settings.service_backlog_months ?? 12}
                onChange={(e) => patch({ service_backlog_months: e.target.value === "" ? 1 : parseInt(e.target.value) })}
                className="h-7 w-16 text-xs" data-testid="input-service-backlog-months" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">Past-due Spread (mo)</div>
              <Input type="number" min={1} max={36} value={settings.past_due_months}
                onChange={(e) => patch({ past_due_months: e.target.value === "" ? 1 : parseInt(e.target.value) })}
                className="h-7 w-16 text-xs" data-testid="input-pastdue-months" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">Revenue Adj +/-</div>
              <PctInput value={settings.revenue_adjustment_pct} onChange={(v) => patch({ revenue_adjustment_pct: v })} testid="input-revenue-adj" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">Auto Adjust Range ($/mo)</div>
              <div className="flex items-center gap-1.5">
                <input type="checkbox" checked={settings.smooth_enabled !== false}
                  onChange={(e) => patch({ smooth_enabled: e.target.checked })}
                  className="h-4 w-4 accent-[#007BFF] cursor-pointer" title="Auto-adjust revenue into this band"
                  data-testid="smooth-enabled" />
                <BandInput value={settings.smooth_min ?? 1700000} onChange={(v) => patch({ smooth_min: v })}
                  disabled={settings.smooth_enabled === false} testid="input-smooth-min" />
                <span className="text-[10px] text-slate-400">to</span>
                <BandInput value={settings.smooth_max ?? 2200000} onChange={(v) => patch({ smooth_max: v })}
                  disabled={settings.smooth_enabled === false} testid="input-smooth-max" />
              </div>
            </div>
            <div className="ml-auto text-right">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-0.5">{viewLabel} EBITDA</div>
              <div className={cn("text-sm font-bold tabular-nums", wsum(calc.ebitda) < 0 ? "text-red-600" : "text-emerald-600")} data-testid="forecast-ebitda-total">
                {fmtK(wsum(calc.ebitda))} <span className="text-[10px] font-medium text-slate-400">({wpct(calc.ebitda).toFixed(1)}%)</span>
              </div>
            </div>
          </div>

          {/* Revenue vs EBITDA margin trend removed per user request */}

          {view === "board" ? (
            <BoardSummary data={data} calc={calc} ci={ci} />
          ) : (
          <>
          {/* P&L grid */}
          <div className="overflow-x-auto" data-testid="forecast-grid">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-slate-200 bg-white">
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 sticky left-0 bg-white z-10 min-w-[190px]">Line Item</th>
                  <th className="text-center px-2 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 sticky left-[190px] bg-white z-10 border-r border-slate-200 min-w-[70px]">% Rev</th>
                  {months.map((m, i) => (
                    <th key={m.key} className={cn(
                      "text-right px-1.5 py-2 text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap align-top",
                      vs + i < ci ? "text-amber-600" : "text-slate-500"
                    )}>
                      {m.label}{!data.actuals_map?.[m.key] && vs + i < ci ? " ·A" : ""}
                      {data.actuals_map?.[m.key] && (
                        <div className="text-[8px] font-bold text-emerald-600 normal-case tracking-normal" data-testid={`actuals-tag-${m.key}`}>Actuals</div>
                      )}
                    </th>
                  ))}
                  <th className="text-right px-2 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600 border-l-2 border-slate-200 bg-slate-50">Total</th>
                </tr>
              </thead>
              <tbody>
                {/* Revenue */}
                {view === "this_fy" && (
                  <EditableMoneyRow label="Actuals (past months)" settingsKey="actuals" arr={calc.actuals} testid="row-actuals" pastOnly />
                )}
                <Row label="Backlog Revenue" arr={calc.backlog} pctCell={pctLabel(wpct(calc.backlog))} bg="bg-emerald-50/40" testid="row-backlog-revenue" />
                <Row label="Overbilling Spread" arr={calc.overbilling} pctCell={pctLabel(wpct(calc.overbilling))} bg="bg-emerald-50/40" testid="row-overbilling" />
                <Row label="Service Backlog" arr={calc.serviceBacklog} pctCell={pctLabel(wpct(calc.serviceBacklog))} bg="bg-emerald-50/40" testid="row-service-backlog" />
                <Row label="Pipeline Revenue" arr={calc.sales} pctCell={pctLabel(wpct(calc.sales))} bg="bg-emerald-50/40" testid="row-sales-revenue" />
                <AdjustmentRow label="New Sales (auto)" arr={calc.newSales} settingsKey="new_sales" lockUntil={ci + 3} testid="row-new-sales" />
                <AdjustmentRow label="Revenue Adjustment (auto)" arr={calc.revAdjust} testid="row-revenue-adjustments" />
                <Row label="Total Revenue" arr={calc.revenue} bold pctCell={pctLabel(100, true)} bg="bg-emerald-100/50" testid="row-total-revenue" />

                {/* Direct costs (collapsible) */}
                <Row label="Direct Costs" arr={calc.direct} bold
                  onToggle={() => setExpanded((e) => ({ ...e, direct: !e.direct }))} isExpanded={expanded.direct}
                  pctCell={<PctInput value={settings.direct_costs_pct} onChange={setDirectTotal} testid="grid-direct-pct" />}
                  bg="bg-amber-50/40" testid="row-direct-costs" />
                {expanded.direct && calc.directRows.map((r, i) => (
                  <Row key={r.name} label={r.name} indent arr={r.vals}
                    pctCell={<PctInput value={settings.direct_items[i].pct} onChange={(v) => patchDirectItem(i, v)} testid={`direct-pct-${i}`} />}
                    testid={`row-direct-${i}`} />
                ))}
                <Row label="Overall Direct GM" arr={calc.directGM} bold red pctOfArr={calc.revenue}
                  pctCell={pctLabel(wpct(calc.directGM), true)} bg="bg-emerald-50/60" testid="row-direct-gm" />

                {/* Indirect — single 10% line (no sub-rows) */}
                <Row label="Indirect Costs" arr={calc.indirectTotal} bold
                  pctCell={<PctInput value={Math.round(calc.indirectPctSum * 100) / 100} onChange={setCategoryTotal("indirect_items")} testid="input-indirect-total-pct" />}
                  bg="bg-orange-200/70" testid="row-indirect-total" />

                {/* Gross margin — % computed per month from actual sums */}
                <Row label="Gross Margin" arr={calc.gross} bold red pctOfArr={calc.revenue}
                  pctCell={pctLabel(wpct(calc.gross), true)} bg="bg-emerald-100/50" testid="row-gross-margin" />

                {/* Overhead (collapsible, header above breakdown) */}
                <Row label="Overhead Costs" arr={calc.overheadTotal} bold
                  onToggle={() => setExpanded((e) => ({ ...e, overhead: !e.overhead }))} isExpanded={expanded.overhead}
                  pctCell={pctLabel(wpct(calc.overheadTotal), true)}
                  bg="bg-orange-200/70" testid="row-overhead-total" />
                {expanded.overhead && calc.overheadRows.map((r, i) => (
                  <Row key={r.name} label={r.name} indent arr={r.vals}
                    pctCell={<MonthlyInput value={settings.overhead_items[i].monthly} onChange={(v) => patchItem("overhead_items", i, { monthly: v })} testid={`overhead-monthly-${i}`} />}
                    testid={`row-overhead-${i}`} />
                ))}
                {calc.bonusEnabled && (
                  <Row label="Company Bonus (quarterly)" indent arr={calc.bonusRow} pctCell={pctLabel(wpct(calc.bonusRow))} testid="row-bonus" />
                )}

                {/* Bottom line */}
                <Row label="EBITDA" arr={calc.ebitda} bold red pctCell={pctLabel(wpct(calc.ebitda), true)} bg="bg-sky-100/50" testid="row-ebitda" />
                <Row label="ITDA" arr={calc.itda}
                  pctCell={<PctInput value={settings.itda_pct ?? 0} onChange={(v) => patch({ itda_pct: v })} testid="input-itda-pct" />}
                  testid="row-itda" />
                <Row label="Net Income" arr={calc.net} bold red pctCell={pctLabel(wpct(calc.net), true)} bg="bg-emerald-100/60" testid="row-net-income" />
              </tbody>
            </table>
          </div>

          <div className="px-5 py-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
            <span>
              {view === "this_fy" && `Fiscal year ${data.fy_labels?.this_fy} · "·A" months accept actuals · `}
              {view === "next_fy" && `Fiscal year ${data.fy_labels?.next_fy} · `}
              Backlog spread = pipeline curve (mo 3-8 from start: 8/10/20/30/22/10%) · Revenue auto-adjusted into the set band (peaks deferred forward; edited cells lock in; changes auto-save) · Past-due portion spread over first {settings.past_due_months} months · Overbilling {fmtK(settings.overbilling_total)} over {settings.overbilling_months} months{settings.service_backlog_total > 0 ? ` · Service Backlog ${fmtK(settings.service_backlog_total)} over ${settings.service_backlog_months} months` : ""} · Direct = {settings.direct_costs_pct}% of revenue · Indirect = % of revenue (editable) · Overhead = fixed $/mo per line (editable, % of rev computed) · Direct GM = Revenue − Direct · GM = Direct GM − Indirect · EBITDA = GM − Overhead · Net = EBITDA − ITDA
            </span>
            {data.beyond_horizon_total > 0 && <span>{fmtK(data.beyond_horizon_total)} of backlog lands beyond this horizon</span>}
          </div>
          </>
          )}
        </>
      )}
      <BacklogHistoryDialog open={historyOpen} onClose={() => setHistoryOpen(false)} />
      <HistoryLogDialog open={changeLogOpen} onClose={() => setChangeLogOpen(false)} onReverted={load} />
      <ComparisonDialog open={comparisonOpen} onClose={() => setComparisonOpen(false)} />
      <SuggestionsDialog open={suggestOpen} onClose={() => setSuggestOpen(false)} calc={calc} settings={settings} data={data} ci={ci} onPatch={patch} />
      <SalaryAdjustmentsDialog
        open={adjOpen}
        onClose={() => setAdjOpen(false)}
        settings={settings}
        onChange={(list) => patch({ salary_adjustments: list })}
        onBonusChange={(b) => patch({ bonus: b })}
        bonusBase={calc?.bonusBase || 0}
        bonusAutoQuarter={calc?.bonusAutoQ || 0}
        monthOptions={(data?.months || []).slice(ci, ci + 18)}
      />
    </div>
  );
}

function BoardSummary({ data, calc, ci }) {
  const seg = (arr, [a, b]) => arr.slice(a, b).reduce((s, v) => s + v, 0);
  const periods = [
    { label: "Current Year", sub: `FY Forecast · ${data.fy_labels?.this_fy || ""}`, rng: [0, 12], head: "bg-sky-200/70" },
    { label: "Rolling 12", sub: data.fy_labels?.rolling12 || "", rng: [ci, ci + 12], head: "bg-orange-200/80" },
    { label: "Next Year", sub: `Forecast · ${data.fy_labels?.next_fy || ""}`, rng: [12, 24], head: "bg-yellow-200/80" },
  ];
  const revTot = (rng) => seg(calc.revenue, rng);

  const Cell = ({ arr, rng, bold, red }) => {
    const v = seg(arr, rng);
    const t = revTot(rng);
    return (
      <td className="px-4 py-1.5 text-right whitespace-nowrap">
        <span className={cn("tabular-nums text-xs", bold ? "font-bold text-slate-900" : "text-slate-700", red && v < -0.5 && "text-red-600")}>
          ${Math.round(v).toLocaleString("en-US")}
        </span>
        <span className="block text-[9px] text-slate-400 tabular-nums">{t ? ((v / t) * 100).toFixed(1) : "0.0"}% of rev</span>
      </td>
    );
  };

  const BRow = ({ label, arr, bold, bg, indent, red, testid }) => (
    <tr className={cn("border-b border-slate-100", bg)} data-testid={testid}>
      <td className={cn("px-4 py-1.5 text-xs whitespace-nowrap", indent && "pl-9", bold ? "font-bold text-slate-900" : "text-slate-600")}>{label}</td>
      {periods.map((p) => <Cell key={p.label} arr={arr} rng={p.rng} bold={bold} red={red} />)}
    </tr>
  );

  return (
    <div className="p-5" data-testid="board-summary">
      <table className="w-full max-w-3xl border-collapse">
        <thead>
          <tr>
            <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 min-w-[220px]">Summary P&L</th>
            {periods.map((p) => (
              <th key={p.label} className={cn("px-4 py-2 text-center rounded-t-md", p.head)}>
                <div className="text-xs font-bold text-slate-800">{p.label}</div>
                <div className="text-[10px] font-medium text-slate-600">{p.sub}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <BRow label="Total Revenues" arr={calc.revenue} bold bg="bg-emerald-100/60" testid="board-total-revenue" />
          <BRow label="Total Direct Costs" arr={calc.direct} bold bg="bg-amber-50/50" testid="board-direct" />
          {calc.directRows.map((r) => <BRow key={r.name} label={r.name} arr={r.vals} indent testid={`board-dir-${r.name}`} />)}
          <BRow label="Overall Direct GM" arr={calc.directGM} bold red bg="bg-emerald-50/60" testid="board-direct-gm" />
          <BRow label="Indirect Costs" arr={calc.indirectTotal} bold bg="bg-orange-200/60" testid="board-indirect-total" />
          <BRow label="Gross Margin" arr={calc.gross} bold red bg="bg-emerald-100/60" testid="board-gross-margin" />
          <BRow label="Overhead Costs" arr={calc.overheadTotal} bold bg="bg-orange-200/60" testid="board-overhead-total" />
          {calc.overheadRows.map((r) => <BRow key={r.name} label={r.name} arr={r.vals} indent testid={`board-oh-${r.name}`} />)}
          {calc.bonusEnabled && <BRow label="Company Bonus (quarterly)" arr={calc.bonusRow} indent testid="board-bonus" />}
          <BRow label="EBITDA" arr={calc.ebitda} bold red bg="bg-sky-100/60" testid="board-ebitda" />
          <BRow label="ITDA" arr={calc.itda} testid="board-itda" />
          <BRow label="Net Income" arr={calc.net} bold red bg="bg-emerald-100/70" testid="board-net-income" />
        </tbody>
      </table>
      <p className="text-[11px] text-slate-400 mt-3">Same figures as the Board Summary export — use Export → Board Summary for the xlsx.</p>
    </div>
  );
}
